import {
  Activity,
  Building2,
  ChevronDown,
  ClipboardList,
  HelpCircle,
  LogOut,
  MailWarning,
  Settings,
  Shield,
  ToggleLeft,
  Users,
} from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';

// Impersonation state was previously provided here, which unmounted the
// session + banner + End-Session control whenever the super admin navigated
// off /admin/*. Now provided at App level (ImpersonationProvider) and rendered
// app-wide via ImpersonationBanner.

// ── Nav items ─────────────────────────────────────────────────────────────────

const navItems = [
  { to: '/admin', label: 'Platform Health', icon: Activity, end: true },
  { to: '/admin/organisations', label: 'Customers', icon: Building2 },
  { to: '/admin/feature-flags', label: 'Feature Flags', icon: ToggleLeft },
  { to: '/admin/failed-notifications', label: 'Failed Notifications', icon: MailWarning },
  { to: '/admin/impersonation', label: 'Impersonation', icon: Users },
  { to: '/admin/config', label: 'System Config', icon: Settings },
  { to: '/admin/audit-log', label: 'Audit Log', icon: ClipboardList },
];

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// Closes the mobile off-canvas sidebar on tap so the chosen page shows at once.
function AdminNavLink({ item }: { item: (typeof navItems)[number] }) {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuButton asChild>
      <NavLink
        to={item.to}
        end={item.end === true}
        onClick={() => {
          if (isMobile) setOpenMobile(false);
        }}
        className={({ isActive }) =>
          isActive ? 'text-sidebar-primary-foreground bg-sidebar-accent' : ''
        }
      >
        <item.icon className="size-4" />
        <span>{item.label}</span>
      </NavLink>
    </SidebarMenuButton>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

export function SuperAdminLayout() {
  const { user, signOut } = useAuth();

  const handleSignOut = () => {
    void signOut();
  };

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/admin">
                  <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground shrink-0">
                    <Shield className="size-4" />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-semibold tracking-tight text-sm">Secure Labs</span>
                    <span className="text-xs text-muted-foreground">Platform Admin</span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <AdminNavLink item={item} />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => {
                  if (window.Intercom) {
                    window.Intercom('show');
                  } else {
                    window.open('mailto:support@securelabsystems.com', '_blank');
                  }
                }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <HelpCircle className="size-4" />
                <span>Help &amp; Support</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.open('https://status.securelabsystems.com', '_blank')}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Activity className="size-4" />
                <span>System Status</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-[var(--avatar-initials-fg)] text-xs font-semibold">
                        {user ? initials(user.fullName) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex flex-col items-start min-w-0 flex-1">
                      <span className="truncate text-sm font-medium">{user?.fullName}</span>
                      <span className="inline-flex items-center rounded-sm bg-muted px-1 text-[10px] h-4 font-semibold text-muted-foreground mt-0.5">
                        Super Admin
                      </span>
                    </span>
                    <ChevronDown className="size-4 ml-auto shrink-0" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">{user?.fullName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => void handleSignOut()}
                      className="text-destructive focus:text-destructive"
                    >
                      <LogOut className="mr-2 size-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
          <SidebarTrigger className="-ml-1" />
          <div className="flex-1" />
          <NotificationBell to="/admin/notifications" />
          <span className="text-xs text-muted-foreground">Platform Administration</span>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
